import maplibregl from 'maplibre-gl';

/**
 * Utility functions for highlighting and zooming to features on the map
 */

const normalizeGeometryType = (type?: string): string => {
  if (!type) return 'Point';
  const lower = type.toLowerCase();
  switch (lower) {
    case 'point':
      return 'Point';
    case 'multipoint':
      return 'MultiPoint';
    case 'linestring':
      return 'LineString';
    case 'multilinestring':
      return 'MultiLineString';
    case 'polygon':
      return 'Polygon';
    case 'multipolygon':
      return 'MultiPolygon';
    default:
      return type;
  }
};

/**
 * Normalize a Geometry, Feature, or FeatureCollection into a standard GeoJSON FeatureCollection
 */
export const normalizeToFeatureCollection = (input: any): GeoJSON.FeatureCollection => {
  const parsed = typeof input === 'string' ? JSON.parse(input) : input;
  if (!parsed) {
    return { type: 'FeatureCollection', features: [] };
  }

  if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
    return {
      type: 'FeatureCollection',
      features: parsed.features
        .filter((f: any) => f && f.geometry)
        .map((f: any) => ({
          ...f,
          type: 'Feature',
          geometry: {
            ...f.geometry,
            type: normalizeGeometryType(f.geometry.type),
          },
        })),
    };
  }

  if (parsed.type === 'Feature' && parsed.geometry) {
    return {
      type: 'FeatureCollection',
      features: [
        {
          ...parsed,
          type: 'Feature',
          geometry: {
            ...parsed.geometry,
            type: normalizeGeometryType(parsed.geometry.type),
          },
        },
      ],
    };
  }

  // Raw Geometry object
  if (parsed.type && parsed.coordinates) {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            ...parsed,
            type: normalizeGeometryType(parsed.type),
          },
        },
      ],
    };
  }

  return { type: 'FeatureCollection', features: [] };
};

/**
 * Calculate bounds for a Geometry, Feature, or FeatureCollection
 */
const calculateBounds = (input: any): [number, number, number, number] => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  const addCoordinatesToBounds = (coordinates: any) => {
    if (!Array.isArray(coordinates) || coordinates.length === 0) return;
    if (Array.isArray(coordinates[0])) {
      coordinates.forEach((coord: any) => addCoordinatesToBounds(coord));
    } else if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
      const [x, y] = coordinates;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  };

  const fc = normalizeToFeatureCollection(input);
  fc.features.forEach((feature: any) => {
    if (feature.geometry?.coordinates) {
      addCoordinatesToBounds(feature.geometry.coordinates);
    }
  });

  if (minX === maxX && minY === maxY && Number.isFinite(minX) && Number.isFinite(minY)) {
    const padding = 0.001;
    return [minX - padding, minY - padding, maxX + padding, maxY + padding];
  }

  return [minX, minY, maxX, maxY];
};

/**
 * Clean up highlight layers and optionally the source for a given sourceId
 */
export const cleanupHighlightLayers = (
  map: maplibregl.Map,
  sourceId: string,
  removeSource: boolean = true,
) => {
  const layerIds = [
    `${sourceId}-fill`,
    `${sourceId}-line`,
    `${sourceId}-polygon-line`,
    `${sourceId}-circle`,
  ];

  try {
    layerIds.forEach((id) => {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    });

    const styleLayers = map.getStyle()?.layers || [];
    styleLayers.forEach((layer: any) => {
      if (layer.source === sourceId && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }
    });

    if (removeSource && map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  } catch (error) {
    // Ignore cleanup errors
  }
};

/**
 * Add simple geometry highlight layers (circle for Point, line for LineString, fill + line for Polygon)
 * Shared between CollectDataTab ("Dữ liệu thu thập") and Element / Map feature highlighting.
 */
export const addSimpleHighlightLayers = (
  mapInstance: maplibregl.Map,
  sourceId: string,
  layerPrefix: string,
  featureCollection: GeoJSON.FeatureCollection,
) => {
  const features = featureCollection.features || [];
  const hasPoint = features.some((f) => f.geometry?.type?.toLowerCase().includes('point'));
  const hasLine = features.some((f) => f.geometry?.type?.toLowerCase().includes('line'));
  const hasPolygon = features.some((f) => f.geometry?.type?.toLowerCase().includes('polygon'));

  if (hasPolygon) {
    mapInstance.addLayer({
      id: `${layerPrefix}-fill`,
      type: 'fill',
      source: sourceId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': 'cyan',
        'fill-opacity': 0.5,
      },
    });
    mapInstance.addLayer({
      id: hasLine ? `${layerPrefix}-polygon-line` : `${layerPrefix}-line`,
      type: 'line',
      source: sourceId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'line-color': 'cyan',
        'line-width': 5,
        'line-opacity': 1,
      },
    });
  }

  if (hasLine) {
    mapInstance.addLayer({
      id: `${layerPrefix}-line`,
      type: 'line',
      source: sourceId,
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': '#44ff44',
        'line-width': 5,
        'line-opacity': 1,
      },
    });
  }

  if (hasPoint) {
    mapInstance.addLayer({
      id: `${layerPrefix}-circle`,
      type: 'circle',
      source: sourceId,
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-color': '#ff4444',
        'circle-radius': 8,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-opacity': 0.8,
      },
    });
  }
};

/**
 * Clear persistent highlight layers on the map
 */
export const clearHighlight = (map?: maplibregl.Map) => {
  const mapInstance = map || window.map;
  if (!mapInstance) return;
  cleanupHighlightLayers(mapInstance, 'highlight', true);
};

/**
 * Highlight a Geometry, Feature, or FeatureCollection on the map using simple geometry layers
 * (shared style with "Dữ liệu thu thập", without padded rectangle bounding boxes)
 */
export const highlightFeatureCollection = (featureCollection: any, map?: maplibregl.Map) => {
  const mapInstance = map || window.map;
  if (!mapInstance || !featureCollection) return;

  try {
    const normalizedFC = normalizeToFeatureCollection(featureCollection);
    if (!normalizedFC.features.length) return;

    const sourceId = 'highlight';
    cleanupHighlightLayers(mapInstance, sourceId, true);

    mapInstance.addSource(sourceId, {
      type: 'geojson',
      data: normalizedFC as any,
    });

    addSimpleHighlightLayers(mapInstance, sourceId, sourceId, normalizedFC);
  } catch (error) {
    console.error('Error highlighting feature collection on map:', error);
  }
};

/**
 * Main function to zoom to and highlight a feature on the map
 * Nhấp nháy 3 lần rồi biến mất hoàn toàn
 */
export const zoomToFeature = async (geometry: any, map?: maplibregl.Map) => {
  const mapInstance = map || window.map;

  if (!mapInstance || !geometry) {
    console.warn('Map instance or geometry not available');
    return;
  }

  const sourceId = 'blink-highlight';

  try {
    const normalizedFC = normalizeToFeatureCollection(geometry);
    if (!normalizedFC.features.length) return;

    // Clean up any existing blink layers
    cleanupHighlightLayers(mapInstance, sourceId, true);

    // Calculate bounds and zoom to feature
    const bbox = calculateBounds(normalizedFC);

    mapInstance.fitBounds(bbox as any, {
      duration: 500,
      padding: 50,
      maxZoom: 18,
    });

    // Wait for zoom to complete
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Clean up any existing blink layers before adding source
    cleanupHighlightLayers(mapInstance, sourceId, true);

    mapInstance.addSource(sourceId, {
      type: 'geojson',
      data: normalizedFC as any,
    });

    // Nhấp nháy 3 lần
    for (let i = 0; i < 3; i++) {
      addSimpleHighlightLayers(mapInstance, sourceId, sourceId, normalizedFC);

      // Wait 200ms (show)
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Remove layers (hide), keep source
      cleanupHighlightLayers(mapInstance, sourceId, false);

      // Wait 200ms (hide)
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    // Final cleanup
    cleanupHighlightLayers(mapInstance, sourceId, true);
  } catch (error) {
    console.error('Error highlighting feature on map:', error);
    cleanupHighlightLayers(mapInstance, sourceId, true);
  }
};

export default {
  zoomToFeature,
  highlightFeatureCollection,
  clearHighlight,
  addSimpleHighlightLayers,
  cleanupHighlightLayers,
  normalizeToFeatureCollection,
};